'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from 'cn';
import { Bell, CreditCard, FileText, PenLine, ShieldCheck } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ClaiOrb } from '@/app/onboarding/_components/workspace-setup/clai-orb';
import { useTicker } from '@/app/onboarding/_components/workspace-setup/previews/use-ticker';
const FEATURES = [{
  icon: PenLine,
  label: 'collect signatures'
}, {
  icon: FileText,
  label: 'create contracts'
}, {
  icon: CreditCard,
  label: 'handle payments'
}, {
  icon: ShieldCheck,
  label: 'keep deals compliant'
}, {
  icon: Bell,
  label: 'keep everyone posted'
}] as const;

/** Distance from the orb to each tile's centre, in px. */
const ORBIT_RADIUS = 104;
const ORBIT_SECONDS = 30;

/**
 * Clai's orb with the product's features circling it. One
 * tile lights up at a time while the caption names it.
 */
export function ClaiHubPreview() {
  const $ = _c(27);
  const reduceMotion = useReducedMotion();
  const active = useTicker(FEATURES.length, 800);
  let t0;
  if ($[0] !== reduceMotion) {
    t0 = degrees => reduceMotion ? undefined : {
      animate: {
        rotate: degrees
      },
      transition: {
        duration: ORBIT_SECONDS,
        repeat: Number.POSITIVE_INFINITY,
        ease: "linear" as const
      }
    };
    $[0] = reduceMotion;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const spin = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="border-primary/30 absolute inset-6 rounded-full border border-dashed" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== spin) {
    t2 = spin(360);
    $[3] = spin;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== active || $[6] !== spin) {
    t3 = FEATURES.map((feature, i) => {
      const angle = i / FEATURES.length * Math.PI * 2 - Math.PI / 2;
      const isActive = i === active;
      return <motion.div key={feature.label} className="absolute top-1/2 left-1/2 -mt-7 -ml-7 size-14" style={{
        x: Math.cos(angle) * ORBIT_RADIUS,
        y: Math.sin(angle) * ORBIT_RADIUS
      }} {...spin(-360)}><motion.div className={cn("bg-card flex size-full items-center justify-center rounded-2xl border shadow-sm transition-colors", {
          "border-primary text-primary": isActive,
          "border-border text-muted-foreground": !isActive
        })} animate={{
          scale: isActive ? 1.18 : 1
        }} transition={{
          type: "spring",
          stiffness: 300,
          damping: 18
        }}><feature.icon className="size-6" /></motion.div></motion.div>;
    });
    $[5] = active;
    $[6] = spin;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = <motion.div className="absolute inset-0" {...t2} data-sentry-element="motion.div" data-sentry-source-file="clai-hub-preview.tsx">{t3}</motion.div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== active) {
    t5 = <div className="absolute inset-0 flex items-center justify-center"><ClaiOrb size={84} pulseKey={active} data-sentry-element="ClaiOrb" data-sentry-source-file="clai-hub-preview.tsx" /></div>;
    $[11] = active;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t4 || $[14] !== t5) {
    t6 = <div className="relative size-64">{t1}{t4}{t5}</div>;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="text-muted-foreground">Clai can</span>;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = FEATURES[active]?.label;
  let t10;
  let t11;
  let t12;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      y: 12,
      opacity: 0
    };
    t10 = {
      y: 0,
      opacity: 1
    };
    t11 = {
      y: -12,
      opacity: 0
    };
    t12 = {
      duration: 0.25,
      ease: "easeOut"
    };
    $[17] = t10;
    $[18] = t11;
    $[19] = t12;
    $[20] = t9;
  } else {
    t10 = $[17];
    t11 = $[18];
    t12 = $[19];
    t9 = $[20];
  }
  const t13 = FEATURES[active]?.label;
  let t14;
  if ($[21] !== t13 || $[22] !== t8) {
    t14 = <div className="border-border bg-card/80 mt-4 flex items-center gap-1 overflow-hidden rounded-full border px-4 py-1.5 text-sm backdrop-blur-sm">{t7}<AnimatePresence mode="wait" initial={false} data-sentry-element="AnimatePresence" data-sentry-source-file="clai-hub-preview.tsx"><motion.span key={t8} className="text-foreground font-medium" initial={t9} animate={t10} exit={t11} transition={t12} data-sentry-element="motion.span" data-sentry-source-file="clai-hub-preview.tsx">{t13}</motion.span></AnimatePresence></div>;
    $[21] = t13;
    $[22] = t8;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t14 || $[25] !== t6) {
    t15 = <div className="flex w-full flex-col items-center" data-sentry-component="ClaiHubPreview" data-sentry-source-file="clai-hub-preview.tsx">{t6}{t14}</div>;
    $[24] = t14;
    $[25] = t6;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  return t15;
}